import { useId } from 'vue'

export const useFormFieldProps = () => ({
  modelValue: {},
  id: {
    type: [String, Number]
  },
  name: {
    type: [String, Number]
  },
  label: {
    type: String
  },
  errors: {
    type: [Array, String, Object]
  },
  info: {
    type: String
  },
  inline: {
    type: Boolean,
    default: false
  },
  grid: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: [String, Boolean],
    default: false
  },
  tooltipClickOnly: {
    type: Boolean,
    default: false
  },
  validate: {
    type: Object as PropType<Validate>
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  }
})

export const useFormField = (props: any, emit: Function) => {
  let validateTimeout: any = null
  let uniqueID = props.id || useId()
  const errorList = ref([])
  const hasFocus = ref(false)

  const formLabelProps = computed(() => {
    return {
      forID: uniqueID,
      label: props.label,
      tooltip: props.tooltip,
      hasTooltip: hasTooltip.value,
      tooltipClickOnly: props.tooltipClickOnly,
      required: isRequired.value
    }
  })

  const isDisabled = computed(() => props.disabled !== false)

  const isRequired = computed(() => props.required !== false)

  const isError = computed(() => mergedErrors.value?.length > 0)

  const isInline = computed(() => props.inline !== false)

  const isGrid = computed(() => props.grid !== false)

  const hasTooltip = computed(() => (props.tooltip !== false && props.tooltip !== '') || useSlots().tooltip !== undefined)

  const hasValue = computed(() => {
    return (
      ![null, undefined].includes(fieldValue.value) &&
      (fieldValue.value.toString().length > 0 || fieldValue.value.toString() !== '')
    )
  })

  const mergedErrors = computed(() => {
    const errors: string | Array<string> = errorList.value ? [...errorList.value] : []
    if (props.errors) {
      const e = !Array.isArray(props.errors) ? [props.errors] : props.errors
      errors.unshift(...e)
    }

    return errors
  })

  const fieldValue = useComputedModel(props, emit)

  function setFocus(state: boolean) {
    hasFocus.value = state
  }

  function triggerValidate() {
    if(props.validate) {
      return useValidate(props.validate, props.modelValue, { ...props, errorList, fieldValue })
    }

    // is error
    return false
  }

  watch(fieldValue, () => {
    if (props.validate) {
      clearTimeout(validateTimeout)
      validateTimeout = setTimeout(() => {
        useValidate(props.validate, props.modelValue, { ...props, errorList, fieldValue })
      }, 500)
    }
  })

  return {
    uniqueID,
    fieldValue,
    isError,
    isInline,
    isGrid,
    isDisabled,
    isRequired,
    hasValue,
    hasFocus,
    hasTooltip,
    mergedErrors,
    formLabelProps,
    errorList,
    triggerValidate,
    setFocus,
  }
}

const getMessage = (message: any, type: string) => {
  if (message === undefined) return undefined
  if (typeof message === 'string') return message
  else return message[type]
}

const validate = {
  required(value: any, attributes: any) {
    return {
      isError: [undefined, null].includes(value) || value.length === 0 || value === '' || value === false,
      message: getMessage(attributes.message, 'required') || 'Ovo polje je obavezno'
    }
  },

  number(value: string | number, attributes: any, instance: any) {
    if (attributes.strict) {
      value = parseInt(value.toString().replace(/[^\d]/g, ''))
      instance.fieldValue.value = !isNaN(value) ? value : ''
    } else {
      return {
        isError: !/^\d*$/.test(value.toString()),
        message: getMessage(attributes.message, 'number') || 'Must be a number'
      }
    }
  },

  phone(value: string, attributes: any, instance: any) {
    if (attributes.strict) instance.fieldValue.value = value.replace(/[^\+\d]/g, '')
    else {
      return {
        isError: !/[^\+\d]*$/.test(value),
        message: getMessage(attributes.message, 'phone') || 'Must be a phone number'
      }
    }
  },

  decimal(value: string, attributes: any, instance: any) {
    if (attributes.strict) {
      instance.fieldValue.value = value.toString().replace(/[^\d|\.]/g, '')
      if (instance.fieldValue.value.split('.').length - 1 > 1) instance.fieldValue.value = value.slice(0, -1)
    }
    return {
      isError: !/^(?!(0\d))\d+\.\d+$/.test(value),
      message: getMessage(attributes.message, 'decimal') || 'Must be a decimal number'
    }
  },

  email(value: string, attributes: any) {
    return {
      isError:
        !/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          value
        ),
      message: getMessage(attributes.message, 'email') || 'Molimo unesite ispravnu e-mail adresu'
    }
  },

  minlength(value: any, attributes: any, instance: any) {
    let minlength = parseInt(attributes.minlength || instance.minlength)
    value = Array.isArray(value) ? value : value.toString()
    return {
      isError: value.length < minlength,
      message: getMessage(attributes.message, 'minlength') || `Min length is ${minlength}`
    }
  },

  maxlength(value: any, attributes: any, instance: any) {
    let maxlength = parseInt(attributes.maxlength || instance.maxlength)
    value = Array.isArray(value) ? value : value.toString()
    if (attributes.strict) instance.fieldValue.value = instance.fieldValue.value.slice(0, maxlength)
    else {
      return {
        isError: value.length > maxlength,
        message: getMessage(attributes.message, 'maxlength') || `Max length is ${maxlength}`
      }
    }
  },

  min(value: number, attributes: any, instance: any) {
    let min = parseInt(attributes.min || instance.min)
    return {
      isError: value < min,
      message: getMessage(attributes.message, 'min') || `Number must be larger than ${min} or equal`
    }
  },

  max(value: number, attributes: any, instance: any) {
    let max = parseInt(attributes.max || instance.max)
    return {
      isError: value > max,
      message: getMessage(attributes.message, 'max') || `Number must be larger than ${max} or equal`
    }
  },

  pattern(value: string, attributes: any) {
    let pattern = ''
    for (let p of attributes.pattern) {
      if (/^((?![`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]).)*$/.test(p)) pattern += '.'
      else pattern += '\\' + p
    }
    return {
      isError: !new RegExp(pattern + '$').test(value),
      message: getMessage(attributes.message, 'pattern') || `Pattern must be ${attributes.pattern}`
    }
  },

  date(value: string, attributes: any) {
    let dateHelper = value.split(attributes.delimiter)
    let formatHelper: Array<string> = attributes.pattern.split(attributes.delimiter)
    let day = dateHelper[formatHelper.findIndex(i => i.toLowerCase().includes('d'))]
    let month = dateHelper[formatHelper.findIndex(i => i.toLowerCase().includes('m'))]
    let year = dateHelper[formatHelper.findIndex(i => i.toLowerCase().includes('y'))]
    return {
      isError:
        [day, month, year].some(i => i === undefined || i.length === 0) || !Date.parse(`${month}-${day}-${year}`),
      message: getMessage(attributes.message, 'date') || `Date is not valid`
    }
  },

  sameAs(value: string, attributes: any, instance: any) {
    return {
      isError: attributes.compareValue ? attributes.compareValue !== value : false,
      message: getMessage(attributes.message, 'sameAs') || `This field value and the ${attributes.compareFieldName || attributes.compareValue} field value must be the same`
    }
  },

  password(value: string, attributes: any, instance: any) {
    // Minimum eight characters, maximum 16 characters, at least one uppercase letter, one lowercase letter, one number and one special character
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};:,./<>?\\|`~]).{8,16}$/;
    return {
      isError: !regex.test(value),
      message: getMessage(attributes.message, 'password') || `This field must contain at least one uppercase letter, one lowercase letter, one number and one special character`
    }
  },
} as any

export const useValidate = (attributes: any, value: any, instance: any) => {
  const response = []
  if (typeof attributes.type === 'string') attributes.type = [attributes.type]
  for (let t of attributes.type) {
    if (typeof t === 'string') {
      const e = validate[t](value, attributes, instance)
      if (e && e.isError) response.push(validate[t](value, attributes, instance))
    } else {
      const isError: boolean = !(<any>Object).values(t)[0](value)
      const key = Object.keys(t)[0]
      if (isError && attributes.message && attributes.message[key]) {
        response.push({
          isError,
          message: attributes.message[key]
        })
      }
    }
  }

  if (response.length > 0) instance.errorList.value = response.map(i => i.message)
  else if (instance?.errorList?.value?.length > 0) instance.errorList.value = undefined

  return (instance?.errorList?.value?.length || instance?.errors?.length || response.length) ? true : false
}

export const useFormIsValid = (elements: any[]) => {
  let isFormValid = true
  elements = elements.filter(i => i !== null)
  for (let instance of elements) {
    const hasErrors = instance.triggerValidate()
    if(isFormValid && hasErrors) isFormValid = false
  }

  return isFormValid;
}